import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import AuthRequiredGate from "./AuthRequiredGate";
import Icon from "./Icon";
import AiSidebar from "./AiSidebar";
import AiPropertyCard from "./AiPropertyCard";
import ConfirmDialog from "./Confirmdialog";
import GuidedSearch, { GuidedSearchBody, GuidedSearchComposer, clearDraft } from "./GuidedSearch";
import { ENDPOINTS } from "./Constants";
import { authedFetch, uid, withDisclosures } from "./Helpers";
import { sessionStorageService, authenticationApi } from "../../features/authentication";

import "../../styles/ai/ai-variables.css";
import "../../styles/ai/ai-layout.css";
import "../../styles/ai/ai-chatbox.css";
import "../../styles/ai/ai-search.css";
import "../../styles/ai/ai-personal-agent.css";

// Display title for this page's saved chat-history row.
const PERSONAL_AGENT_CHAT_TITLE = "Personal Agent";

// Identifies this page's saved chat-history row among the user's other
// saved chats -- a real column (see backend migration 0008), not a title
// string match. Must match CHAT_SOURCE in personal_agent/chat_sync.py.
const PERSONAL_AGENT_CHAT_SOURCE = "personal_agent";

// Matches personal_agent_chat_id() in personal_agent/service.py -- JWT `sub`
// is always the email (security/jwt.py), so this is safe to compute
// client-side without waiting on a server round trip.
const personalAgentChatId = (email) => `personal-agent-${email}`;

/**
 * "Personal Agent" — a thin page wrapper around <GuidedSearch/> (the 6-question
 * onboarding wizard + confirmation gate + resulting chat, see GuidedSearch.jsx),
 * providing this page's own chrome: sidebar, topbar, auth gate, the
 * Active/Recents dashboard, and pause/resume. Once onboarding completes, the
 * conversation continues as a real Ask AI chat: same sidebar, same AiMessage
 * rendering, same composer -- both literally hit POST /api/ai/ask with the
 * same chat_id, narrowed via ConversationContext.merge_with_memory.
 */
export default function PersonalAgentPage() {
  const navigate = useNavigate();
  const token = sessionStorageService.get();
  const isAuthenticated = !!token;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { active, onboardingCompletedAt, profile }
  const [error, setError] = useState("");
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [chatId, setChatId] = useState(null); // ties the completion search to POST /api/ai/ask for refinement
  const [messages, setMessages] = useState([]); // chat turns once onboarding has completed
  const [backendChatId, setBackendChatId] = useState(null); // /api/ai/chat-history row id, once saved
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 640);

  // Same placeholder-then-hydrate pattern AiSearchPage.js uses for its own
  // sidebar footer -- decode the JWT for an instant name, then replace it
  // with the real one once GET /api/auth/me resolves.
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const email = payload.email || payload.sub || "";
        return {
          name: email.split("@")[0] || "User", email, role: "Member",
          avatar: null, initials: (email || "U").charAt(0).toUpperCase(),
        };
      }
    } catch { /* silent */ }
    return { name: "Guest", email: "", role: "Free Plan", avatar: null, initials: "G" };
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    authenticationApi.getCurrentUser()
      .then((data) => {
        if (!data?.fullName) return;
        setCurrentUser((prev) => ({
          ...prev,
          name: data.fullName,
          email: data.email || prev.email,
          avatar: data.avatarUrl || prev.avatar,
          initials: data.fullName.charAt(0).toUpperCase(),
        }));
      })
      .catch(() => { /* placeholder from the JWT stays */ });
  }, [isAuthenticated]);

  // AiSidebar expects a chat list -- Personal Agent only ever has the one
  // standing conversation, so this is a single-element list rather than
  // AiSearchPage's full multi-chat history apparatus.
  const chats = useMemo(() => {
    if (!chatId) return [];
    return [{ id: chatId, title: "Personal Agent", messages, createdAt: Date.now() }];
  }, [chatId, messages]);

  const loadMatches = useCallback(async () => {
    if (!token) return;
    setMatchesLoading(true);
    try {
      const rows = await authedFetch(ENDPOINTS.PERSONAL_AGENT_MATCHES, token);
      setMatches(rows || []);
    } catch {
      // Recents is supplementary -- a failed load just shows an empty list.
    } finally {
      setMatchesLoading(false);
    }
  }, [token]);

  // Ask AI's own chats autosave to this same /api/ai/chat-history backend
  // (AiSearchPage.js's saveChatToBackend) -- Personal Agent's completion
  // chat didn't, so refreshing the page silently dropped it in favor of the
  // Recents dashboard. Mirrors that same create-vs-update-by-id pattern here.
  const tryRestoreChat = useCallback(async () => {
    try {
      const sessions = await authenticationApi.listChatHistory();
      const saved = (sessions || []).find((s) => s.source === PERSONAL_AGENT_CHAT_SOURCE);
      if (!saved || !saved.messages?.length) return false;
      setMessages(saved.messages);
      setBackendChatId(saved.id);
      setChatId(personalAgentChatId(currentUser.email));
      return true;
    } catch {
      return false;
    }
  }, [currentUser.email]);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    (async () => {
      try {
        const data = await authedFetch(ENDPOINTS.PERSONAL_AGENT_STATUS, token);
        setStatus(data);
        if (data?.active) {
          const restored = await tryRestoreChat();
          if (!restored) {
            await loadMatches();
          }
        }
        // `active: false` covers two very different accounts: never started
        // onboarding, and paused *after* completing it -- onboardingCompletedAt
        // is what tells them apart. Only the first should ever show the
        // wizard; GuidedSearch itself resumes a local draft or starts fresh.
      } catch (err) {
        setError(err.message || "Couldn't load your Personal Agent.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, token, loadMatches, tryRestoreChat]);

  // Best-effort, debounced save -- same 1.5s-after-last-change pattern as
  // AiSearchPage.js's own autosave. Covers both the completion message and
  // every later refinement turn, so a refresh always shows the up-to-date
  // conversation instead of whatever was on screen at completion time.
  useEffect(() => {
    if (!token || messages.length === 0) return;
    const timer = setTimeout(async () => {
      const firstAi = messages.find((m) => m.role === "ai");
      const payload = {
        title: PERSONAL_AGENT_CHAT_TITLE,
        preview: (firstAi?.text || "").slice(0, 60),
        messages,
        source: PERSONAL_AGENT_CHAT_SOURCE,
      };
      try {
        if (backendChatId) {
          await authenticationApi.updateChatSession(backendChatId, payload);
        } else {
          const saved = await authenticationApi.saveChatSession(payload);
          setBackendChatId(saved.id);
        }
      } catch {
        // Local chat state is unaffected either way -- try again next change.
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [messages, token, backendChatId]);

  // Confirming a search no longer activates the standing subscription
  // (guided-search plan R2/D12) -- only an explicit subscribe does, via
  // handleSubscribedChange below. This just keeps the displayed profile
  // current.
  const handleGuidedSearchCompleted = useCallback((profile) => {
    setStatus((prev) => ({ ...(prev || {}), profile }));
  }, []);

  const handleSubscribedChange = useCallback((subscribed) => {
    setStatus((prev) => (prev ? { ...prev, active: subscribed } : prev));
  }, []);

  const goToNewChat = useCallback(() => navigate("/ai-search"), [navigate]);

  const togglePersonalAgent = useCallback(async () => {
    const wasActive = status?.active;
    const endpoint = wasActive ? ENDPOINTS.PERSONAL_AGENT_PAUSE : ENDPOINTS.PERSONAL_AGENT_RESUME;
    try {
      await authedFetch(endpoint, token, { method: "POST" });
      setStatus((prev) => (prev ? { ...prev, active: !wasActive } : prev));
      if (!wasActive) {
        // Resuming needs the same restore-or-load-matches step the mount
        // effect runs for an already-active account -- without this,
        // clicking Resume showed the Recents dashboard with a chat and
        // matches that were never fetched (both only load when active is
        // true and, until now, pausing skipped that fetch entirely).
        const restored = await tryRestoreChat();
        if (!restored) {
          await loadMatches();
        }
      }
    } catch (err) {
      setError(err.message || "Couldn't update your Personal Agent status.");
    }
  }, [status, token, tryRestoreChat, loadMatches]);

  // On-demand refresh (user-initiated, so it can run any number of times a
  // day) -- reuses POST /search-now, which itself reuses the exact same
  // run_immediate_search the wizard's own completion step calls, just
  // against the saved profile. Surfaces results the same way completing
  // the wizard does: a chat turn, not just a Recents-list refresh.
  const [searchingNow, setSearchingNow] = useState(false);
  const searchNow = useCallback(async () => {
    if (searchingNow) return;
    setSearchingNow(true);
    setError("");
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_SEARCH_NOW, token, { method: "POST" });
      setChatId(result.chatId);
      // Always surface a message, even on zero results -- silently falling
      // back to loadMatches() here threw away the "no matches, here's what
      // would help" text and relaxation chips, which looked indistinguishable
      // from the button doing nothing at all.
      setMessages([{
        id: uid(),
        role: "ai",
        text: withDisclosures(result.results?.message || "No new matches right now — check back later.", result.results?.requirementDisclosures),
        hasResults: result.results?.hasResults || false,
        properties: result.results?.properties || [],
        followUps: result.results?.followUps || [],
        relaxationOptions: result.results?.relaxationOptions || [],
        timestamp: Date.now(),
        keepIntroList: true,
      }]);
      if (!result.results?.hasResults) {
        await loadMatches();
      }
    } catch (err) {
      setError(err.message || "Couldn't search again right now. Please try again.");
    } finally {
      setSearchingNow(false);
    }
  }, [searchingNow, token, loadMatches]);

  // "Start over" -- onboarding_completed_at otherwise never clears once
  // set (personal_agent/service.py's stage flow has no other path back to
  // stage 1), so this is the only way to redo the 6 questions rather than
  // being stuck forever with the first set of answers. Destructive enough
  // (throws away the standing subscription's active/completed state) to
  // confirm first, via the same ConfirmDialog other destructive actions in
  // this app use.
  const [startOverConfirmOpen, setStartOverConfirmOpen] = useState(false);
  const [startingOver, setStartingOver] = useState(false);
  const startOver = useCallback(async () => {
    if (startingOver) return;
    setStartingOver(true);
    setError("");
    try {
      await authedFetch(ENDPOINTS.PERSONAL_AGENT_RESET, token, { method: "POST" });
      clearDraft(currentUser.email);
      setMessages([]);
      setChatId(null);
      setBackendChatId(null);
      setMatches([]);
      // Recomputes showGuidedSearch back to its "never onboarded" branch --
      // profile fields are echoed back unchanged (the backend deliberately
      // keeps them, see reset_onboarding's own docstring) so the fresh
      // wizard can still pre-seed from them, same as a brand-new account
      // with casual-chat-derived preferences would.
      setStatus((prev) => ({ ...(prev || {}), active: false, onboardingCompletedAt: null }));
    } catch (err) {
      setError(err.message || "Couldn't start over right now. Please try again.");
    } finally {
      setStartingOver(false);
      setStartOverConfirmOpen(false);
    }
  }, [startingOver, token, currentUser.email]);

  if (!isAuthenticated) {
    return (
      <div className="ai-layout">
        <AuthRequiredGate redirectTo="/personal-agent" />
      </div>
    );
  }

  // Show the wizard for a fresh account (never onboarded), and keep it
  // mounted through to the resulting chat once it completes (messages.length
  // flips it into chat mode inside GuidedSearch, not a page-level swap) --
  // also covers a restored chat, seeded via tryRestoreChat above.
  const showGuidedSearch = !loading && !!status && (
    (!status.active && !status.onboardingCompletedAt) || (status.active && messages.length > 0)
  );

  return (
    <div className="ai-layout">
      {sidebarOpen && <div className="ai-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <AiSidebar
        chats={chats}
        activeChatId={chatId}
        setActiveChatId={() => {}}
        createNewChat={goToNewChat}
        onRenameChat={() => {}}
        collapsed={!sidebarOpen}
        user={currentUser}
      />

      <main className="ai-main">
        <div className="ai-topbar">
          <button className="topbar-btn" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle sidebar">
            <Icon name="sidebar" size={16} />
          </button>
          <div
            className="topbar-logo"
            onClick={() => navigate("/")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/")}
            aria-label="Go to homepage"
          >
            <img src="/logo.png" alt="OGM" className="topbar-logo-img" />
            <span className="topbar-logo-text">
              <span className="topbar-logo-name">One Global Marketplace</span>
            </span>
          </div>
          <span className="topbar-title">Personal Agent</span>
          <div className="topbar-spacer" />
        </div>

        {showGuidedSearch ? (
          <GuidedSearch
            token={token}
            email={currentUser.email}
            initialMessages={messages}
            initialChatId={chatId}
            initialProfile={status?.profile || {}}
            initialSubscribed={!!status?.active}
            onMessagesChange={setMessages}
            onChatIdChange={setChatId}
            onCompleted={handleGuidedSearchCompleted}
            onNoResults={loadMatches}
            onSubscribedChange={handleSubscribedChange}
          >
            <div className="pa-scroll-area">
              <GuidedSearchBody />
            </div>
            <GuidedSearchComposer />
          </GuidedSearch>
        ) : (
          <div className="pa-scroll-area">
            {loading && <div className="pa-loading">Loading your Personal Agent…</div>}

            {!loading && !status && error && (
              <div className="pa-content">
                <div className="pa-dashboard">
                  <p className="pa-empty">{error}</p>
                  <button className="pa-toggle-btn" onClick={() => window.location.reload()}>Try again</button>
                </div>
              </div>
            )}

            {!loading && status?.active && messages.length === 0 && (
              <div className="pa-content">
                <div className="pa-dashboard">
                  <div className="pa-dashboard-header">
                    <span className="pa-status-row">
                      <span className="pa-status-dot" aria-hidden="true" />
                      <span className="pa-stage-label">Active</span>
                    </span>
                    <h2 className="pa-question">Your Personal Agent is active</h2>
                    <p className="pa-intro">We search for new matches every day and notify you by email.</p>
                    <div className="pa-dashboard-actions">
                      <button className="pa-toggle-btn pa-toggle-btn--primary" onClick={searchNow} disabled={searchingNow}>
                        {searchingNow ? "Searching…" : "Search again now"}
                      </button>
                      <button className="pa-toggle-btn" onClick={togglePersonalAgent}>Pause</button>
                      <button className="pa-toggle-btn" onClick={() => setStartOverConfirmOpen(true)}>Start over</button>
                    </div>
                  </div>

                  <h3 className="pa-recents-title">Recents</h3>
                  {matchesLoading && <div className="pa-loading">Loading matches…</div>}
                  {!matchesLoading && matches.length === 0 && (
                    <p className="pa-empty">
                      No matches yet — check back after tomorrow's search, or refine your preferences below.
                    </p>
                  )}
                  <div className="ai-property-results-list" role="list">
                    {matches.map((m) => (
                      <AiPropertyCard
                        key={m.propertyId}
                        property={m.property}
                        engagementSource="personal_agent"
                        onDismiss={() => setMatches((prev) => prev.filter((row) => row.propertyId !== m.propertyId))}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!loading && status && !status.active && (
              <div className="pa-content">
                <div className="pa-dashboard">
                  <p className="pa-empty">
                    Your Personal Agent is paused. Resume it to continue getting daily matches.
                  </p>
                  <div className="pa-dashboard-actions">
                    <button className="pa-toggle-btn pa-toggle-btn--primary" onClick={togglePersonalAgent}>Resume</button>
                    <button className="pa-toggle-btn" onClick={() => setStartOverConfirmOpen(true)}>Start over</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {startOverConfirmOpen && (
        <ConfirmDialog
          title="Start over?"
          message="This resets your Personal Agent so you can redo the 6 questions from scratch. Your saved matches history and chat aren't deleted, but your current preferences will be replaced once you finish the new questions."
          confirmLabel={startingOver ? "Starting over…" : "Start over"}
          onConfirm={startOver}
          onCancel={() => setStartOverConfirmOpen(false)}
        />
      )}
    </div>
  );
}
